import React, { useState } from 'react';
import { 
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  LockClosedIcon,
  EyeIcon,
  ClockIcon,
  UserIcon,
  ComputerDesktopIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import Navbar from './Navbar';

const AdminSecurity = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showApiModal, setShowApiModal] = useState(false);

  const securityStats = [
    {
      label: 'Security Score',
      value: '94%',
      status: 'good',
      icon: ShieldCheckIcon,
      color: 'text-green-500'
    },
    {
      label: 'Active Sessions',
      value: '1,234',
      status: 'normal',
      icon: EyeIcon,
      color: 'text-blue-500'
    },
    {
      label: 'Failed Logins',
      value: '23',
      status: 'warning',
      icon: ExclamationTriangleIcon,
      color: 'text-yellow-500'
    },
    {
      label: 'API Keys',
      value: '12',
      status: 'normal',
      icon: KeyIcon,
      color: 'text-purple-500'
    }
  ];

  const recentActivity = [
    {
      id: 1,
      type: 'login',
      user: 'john.doe@orbitmind.ai',
      action: 'Successful login',
      ip: '192.168.1.100',
      location: 'New York, US',
      timestamp: '2024-02-02 14:30:22',
      status: 'success'
    },
    {
      id: 2,
      type: 'failed_login',
      user: 'unknown@example.com',
      action: 'Failed login attempt',
      ip: '45.123.45.67',
      location: 'Unknown',
      timestamp: '2024-02-02 14:25:15',
      status: 'warning'
    },
    {
      id: 3,
      type: 'api_access',
      user: 'system',
      action: 'API key generated',
      ip: '192.168.1.50',
      location: 'Internal',
      timestamp: '2024-02-02 13:45:08',
      status: 'info'
    },
    {
      id: 4,
      type: 'password_change',
      user: 'sarah.chen@orbitmind.ai',
      action: 'Password changed',
      ip: '192.168.1.75',
      location: 'San Francisco, US',
      timestamp: '2024-02-02 12:20:33',
      status: 'success'
    }
  ];

  const apiKeys = [
    {
      id: 1,
      name: 'Production API',
      key: 'pk_live_****************************',
      created: '2024-01-15',
      lastUsed: '2024-02-02 14:30',
      status: 'active',
      permissions: ['read', 'write']
    },
    {
      id: 2,
      name: 'Development API',
      key: 'pk_test_****************************',
      created: '2024-01-20',
      lastUsed: '2024-02-01 16:45',
      status: 'active',
      permissions: ['read']
    },
    {
      id: 3,
      name: 'Analytics API',
      key: 'pk_analytics_**********************',
      created: '2024-01-25',
      lastUsed: '2024-01-28 09:20',
      status: 'inactive',
      permissions: ['read']
    }
  ];

  const securityPolicies = [
    {
      name: 'Password Policy',
      description: 'Minimum 8 characters, special characters required',
      status: 'enabled',
      lastUpdated: '2024-01-15'
    },
    {
      name: 'Two-Factor Authentication',
      description: 'Required for all admin accounts',
      status: 'enabled',
      lastUpdated: '2024-01-10'
    },
    {
      name: 'Session Timeout',
      description: 'Auto logout after 30 minutes of inactivity',
      status: 'enabled',
      lastUpdated: '2024-01-05'
    },
    {
      name: 'IP Whitelist',
      description: 'Restrict access to specific IP ranges',
      status: 'disabled',
      lastUpdated: '2024-01-01'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'info':
        return <KeyIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-500';
      case 'inactive': return 'bg-gray-500/10 text-gray-400';
      case 'enabled': return 'bg-green-500/10 text-green-500';
      case 'disabled': return 'bg-red-500/10 text-red-500';
      default: return 'bg-gray-500/10 text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Security Center</h1>
            <p className="text-gray-400 mt-2">Monitor and manage system security</p>
          </div>
          <div className="flex items-center gap-4">
            <button className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors border border-gray-700">
              <ArrowPathIcon className="h-5 w-5 inline mr-2" />
              Refresh
            </button>
            <button 
              onClick={() => setShowApiModal(true)}
              className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Generate API Key
            </button>
          </div>
        </div>

        {/* Security Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {securityStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <Icon className={`h-8 w-8 ${stat.color}`} />
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    stat.status === 'good' ? 'bg-green-500/10 text-green-500' :
                    stat.status === 'warning' ? 'bg-yellow-500/10 text-yellow-500' :
                    'bg-blue-500/10 text-blue-500'
                  }`}>
                    {stat.status}
                  </div>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">{stat.label}</p>
                  <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-700 mb-8">
          <nav className="flex gap-8">
            {['overview', 'activity', 'api-keys', 'policies'].map((tab) => (
              <button
                key={tab}
                className={`pb-4 text-sm font-bold capitalize transition-colors ${
                  activeTab === tab
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-400 hover:text-white'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.replace('-', ' ')}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Security Health */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-6">Security Health</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <ShieldCheckIcon className="h-6 w-6 text-green-500" />
                    <div>
                      <p className="text-white font-medium">SSL Certificate</p>
                      <p className="text-gray-400 text-sm">Valid until Dec 2024</p>
                    </div>
                  </div>
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <LockClosedIcon className="h-6 w-6 text-green-500" />
                    <div>
                      <p className="text-white font-medium">Firewall Status</p>
                      <p className="text-gray-400 text-sm">All ports secured</p>
                    </div>
                  </div>
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-3">
                    <KeyIcon className="h-6 w-6 text-yellow-500" />
                    <div>
                      <p className="text-white font-medium">API Security</p>
                      <p className="text-gray-400 text-sm">Rate limiting active</p>
                    </div>
                  </div>
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
                </div>
              </div>
            </div>

            {/* Recent Threats */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-6">Threat Detection</h3>
              <div className="space-y-4">
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                    <span className="text-red-500 font-medium">High Risk</span>
                  </div>
                  <p className="text-white text-sm">Multiple failed login attempts from 45.123.45.67</p>
                  <p className="text-gray-400 text-xs mt-1">2 hours ago</p>
                </div>
                
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                    <span className="text-yellow-500 font-medium">Medium Risk</span>
                  </div>
                  <p className="text-white text-sm">Unusual API usage pattern detected</p>
                  <p className="text-gray-400 text-xs mt-1">6 hours ago</p>
                </div>
                
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                    <span className="text-blue-500 font-medium">Info</span>
                  </div>
                  <p className="text-white text-sm">Security scan completed successfully</p>
                  <p className="text-gray-400 text-xs mt-1">1 day ago</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white">Security Activity Log</h3>
              <p className="text-gray-400 text-sm mt-1">Recent security events and user activities</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Event</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">IP Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {recentActivity.map((activity) => (
                    <tr key={activity.id} className="hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(activity.status)}
                          <span className="text-white text-sm">{activity.action}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {activity.user}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 font-mono">
                        {activity.ip}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {activity.location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {activity.timestamp}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'api-keys' && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white">API Keys</h3>
                  <p className="text-gray-400 text-sm mt-1">Manage API keys and access tokens</p>
                </div>
                <button 
                  onClick={() => setShowApiModal(true)}
                  className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Generate New Key
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Key</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Last Used</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {apiKeys.map((key) => (
                    <tr key={key.id} className="hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-white">{key.name}</div>
                          <div className="text-sm text-gray-400">Created {key.created}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm text-gray-300 bg-gray-700 px-2 py-1 rounded">
                          {key.key}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(key.status)}`}>
                          {key.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {key.lastUsed}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button className="text-blue-400 hover:text-blue-300 transition-colors">
                            Edit
                          </button>
                          <button className="text-red-400 hover:text-red-300 transition-colors">
                            Revoke
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Policies Tab */}
        {activeTab === 'policies' && (
          <div className="space-y-6">
            {securityPolicies.map((policy, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white">{policy.name}</h3>
                    <p className="text-gray-400 text-sm mt-1">{policy.description}</p>
                    <p className="text-gray-500 text-xs mt-2">Last updated: {policy.lastUpdated}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(policy.status)}`}>
                      {policy.status}
                    </span>
                    <button className="text-blue-400 hover:text-blue-300 transition-colors">
                      Configure
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* API Key Modal */}
      {showApiModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Generate API Key</h3>
              <button 
                onClick={() => setShowApiModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Key Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  placeholder="Enter key name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">Permissions</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    <span className="text-white text-sm">Read access</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    <span className="text-white text-sm">Write access</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    <span className="text-white text-sm">Admin access</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowApiModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Generate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSecurity;